/**
 * Which screens each role may open.
 *
 * ONE list, used by BOTH the sidebar and the route guard. They were the
 * obvious place for this app's recurring bug — the same rule written twice,
 * drifting until the menu hides a page the router still serves. A cashier who
 * types /accounting must be bounced by the same list that hid the link.
 *
 * This is convenience and clarity, NOT the security boundary. The boundary is
 * RLS (migration 057): even if a cashier reaches an accounting screen, the
 * database returns no purchases, no expenses, no suppliers and no credentials.
 * Never let a UI check be the only thing standing between a role and data.
 */

export type UserRole = 'owner' | 'cashier' | 'accountant'

/** The cashier's whole app: sell (at the till or the self-checkout kiosk —
 *  a shared „საერთო სალარო" login is a cashier), take a debt payment, read
 *  the manual, and scan a price on a handheld terminal (the terminal page
 *  hides goods-in from her; the count follows the owner's 070 switch). */
const CASHIER_PATHS = ['/pos', '/kiosk', '/credit', '/guide', '/terminal', '/repairs', '/orders', '/kitchen'] as const

/** 1.67 — a waiter (a cashier login linked to a waiter row): his app and the manual. A switched-off waiter
 *  ('off') gets the same two paths and the app shows him only „ანგარიში გათიშულია". */
const WAITER_PATHS = ['/waiter', '/guide'] as const

/** 1.67 — whether this login is a waiter: 'none' (owner, accountant, a shared till, a cashier), 'active', 'off'. */
export type WaiterAccess = 'none' | 'active' | 'off'

/** A cashier login linked to a cashier row of kind 'waiter' is a waiter — fail closed: an inactive row is 'off',
 *  never a shared till (Codex #6). An owner with a stray waiter row stays the owner. */
export function waiterAccessOf(
  userId: string | null,
  role: UserRole | null,
  cashiers: readonly { user_id?: string | null; kind?: 'cashier' | 'waiter'; active: boolean }[],
): WaiterAccess {
  if (!userId || role !== 'cashier') return 'none'
  const mine = cashiers.filter((c) => c.user_id === userId && c.kind === 'waiter')
  if (mine.length === 0) return 'none'
  return mine.some((c) => c.active) ? 'active' : 'off'
}

/** The accountant (094): the books and the shelves behind them — read only.
 *  No till, no staff, no settings: nothing there is theirs to read either. */
const ACCOUNTANT_PATHS = ['/accounting', '/warehouse', '/suppliers', '/credit', '/guide', '/repairs'] as const

/**
 * @param role NULL means hydrate has not answered yet, or the membership could
 *   not be read. Both resolve to the SMALLER set — an unknown role is never
 *   given an owner's screens.
 */
export function canOpen(path: string, role: UserRole | null, waiter: WaiterAccess = 'none'): boolean {
  if (role === 'owner') return true
  const paths: readonly string[] =
    waiter !== 'none' ? WAITER_PATHS : role === 'accountant' ? ACCOUNTANT_PATHS : CASHIER_PATHS
  return paths.some((p) => path === p || path.startsWith(p + '/'))
}

/** Where a cashier lands, and where the guard sends them back to. */
export const CASHIER_HOME = '/pos'

/** Where each role lands; the guard sends a bounced user here. */
export function homeFor(role: UserRole | null, waiter: WaiterAccess = 'none'): string {
  if (waiter !== 'none') return '/waiter'
  return role === 'accountant' ? '/accounting' : CASHIER_HOME
}

/** 094 — the accountant sees what the owner sees and changes nothing. The
 *  database refuses every write anyway; this only hides the buttons. */
export function isReadOnly(role: UserRole | null): boolean {
  return role === 'accountant'
}
